import { CnCOrderStatus } from "./api";

// ITAM-2622 — the adviser profile contract (verified against apoteka-back
// @ cf021847: PharmacistProfileController, PharmacistProfileService,
// PharmacistProfileResponse/Request, FileResponse).

export type PharmacistAvailability = "FREE" | "IN_CALL" | "UNAVAILABLE";

export type PharmacistDayOfWeek =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

export interface PharmacistWorkingHoursEntry {
  dayOfWeek: PharmacistDayOfWeek;
  /** Strictly "HH:mm" — the backend's @JsonFormat rejects "08:00:00" with a 400. */
  availableFrom: string;
  availableUntil: string;
}

export interface PharmacistProfileImageRequest {
  /**
   * CREATE: a placeholder — the backend pairs `images[i]` with `files[i]` by
   * index and overwrites this with a generated name.
   * UPDATE: authored by the client (kept rows reuse their stored location; new
   * files must name the location the file will actually land on). This is the
   * pre-BG-5 contract; see the target contract note in the service.
   */
  location: string | null;
  /** Always null — the backend generates no thumbnails. */
  thumbnail: string | null;
  streaming: string | null;
  type: "IMAGE" | "VIDEO";
  priority: number;
  /** isSmall=1 rows are stored but never returned by the read model. */
  isSmall: 0;
}

export interface PharmacistProfileRequest {
  /** Update lookup; the backend falls back to userId when absent. */
  id?: number;
  /** ALWAYS from the caller's own JWT. */
  userId: number;
  /** Update overwrites unconditionally — always send all four text fields. */
  firstName: string;
  secondName: string;
  licenceNumber: string;
  biography: string;
  images?: PharmacistProfileImageRequest[];
  /** Default false = media untouched. True deletes all rows and rebuilds. */
  updateImages?: boolean;
  /** OMIT to leave the schedule alone; [] clears it. */
  workingHours?: PharmacistWorkingHoursEntry[];
}

export interface PharmacistProfileFileResponse {
  id: number;
  location: string;
  thumbnail: string | null;
  streaming: string | null;
  type: "IMAGE" | "VIDEO";
  priority: number;
  isSmall: number;
  /** @deprecated backward-compatibility alias of `location`. */
  imageLink?: string;
}

export interface PharmacistProfileResponse {
  id: number;
  userId: number;
  firstName: string | null;
  secondName: string | null;
  licenceNumber: string | null;
  biography: string | null;
  images: PharmacistProfileFileResponse[];
  workingHours: PharmacistWorkingHoursEntry[];
  /** ABSENT (NON_NULL) = never enrolled for online consultations. */
  availability?: PharmacistAvailability;
  /** Separately nullable. */
  paused?: boolean;
  /** Atomic pair — both present or both absent (BF-4). */
  marketplaceId?: number;
  branchMarketplaceId?: number;
}

/** The consultation pharmacy context: the profile pair and nothing else. */
export interface ConsultPharmacyPair {
  marketplaceId: number;
  branchMarketplaceId: number;
}

export const PHARMACIST_DAYS: PharmacistDayOfWeek[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

export const PHARMACIST_DAY_LABELS: Record<PharmacistDayOfWeek, string> = {
  MONDAY: "Ponedeljak",
  TUESDAY: "Utorak",
  WEDNESDAY: "Sreda",
  THURSDAY: "Četvrtak",
  FRIDAY: "Petak",
  SATURDAY: "Subota",
  SUNDAY: "Nedelja",
};

// ITAM-2674: adviser stats contract (verified against apoteka-back
// @ e03df517: PharmacistProfileController#getStats, PharmacistStatsResponse).

/**
 * Backend enum order (`CncOrderTransactionStatus`); the UI iterates this,
 * never the JSON key order.
 */
export const CNC_ORDER_STATUSES = [
  "CREATED",
  "PREPARED",
  "SEEN",
  "PROCESSED",
  "CANCELED",
  "REJECTED",
  "TAKEN",
  "NOT_TAKEN",
  "FOR_DELIVERY",
  "IN_DELIVERY",
  "SUSPENDED",
  "CANCELED_BY_USER",
] as const satisfies readonly CnCOrderStatus[];

// Compile-time exhaustiveness: a CnCOrderStatus missing from the tuple above
// fails tsc here.
type MissingStatus = Exclude<
  CnCOrderStatus,
  (typeof CNC_ORDER_STATUSES)[number]
>;
type AssertNever<T extends never> = T;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AllStatusesCovered = AssertNever<MissingStatus>;

/**
 * GET /pharmacist_profile/user_id/{id}/stats. `reservationsByStatus` always
 * carries one key per backend enum value, 12 since ITAM-2858 added
 * CANCELED_BY_USER (zero when empty); an unknown id returns 200 with zeros.
 */
export interface PharmacistStatsResponse {
  /** PHARMACIST_SUPPORT consults by this consultant with a written report. */
  reportsCount: number;
  /** Orders this pharmacist created on a customer's behalf during a consult. */
  reservationsTotal: number;
  reservationsByStatus: Record<CnCOrderStatus, number>;
}
